/*
 * Purpose: Mission knowledge corpus — documents + structured forms (all missions)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 98
 */
import { create } from 'zustand';
import type {
  MissionKnowledgeDoc,
  MissionKnowledgeRequest,
  MissionKnowledgeSessionData,
  MissionKnowledgeSummary,
} from '@/services/mission-knowledge/types';
import {
  DEFAULT_MISSION_AUTOMATION_CONSENT,
  normalizeMissionAutomationConsent,
  setMissionAutomationScope,
  type MissionAutomationConsent,
  type MissionAutomationScope,
} from '@/lib/missionAutomationPolicy';
import {
  formatStructuredAnswersForContext,
  requestIsComplete,
} from '@/lib/missionRequestFields';
import type { MissionRiskApproval } from '@/lib/missionRiskPolicy';
import { recordMissionDossier } from '@/services/mission-live/recordLiveAction';
import {
  deleteMissionDocOnServer,
  retrieveMissionContext,
  syncMissionSessionToServer,
} from '@/services/mission-knowledge/syncApi';
import { retainMissionSessionPolicyAsync } from '@/services/mission-knowledge/persistGenerated';

const STORAGE_PREFIX = 'autoblocks:mission-knowledge:';

function normalizeDoc(raw: Partial<MissionKnowledgeDoc> & { title: string; body: string }): MissionKnowledgeDoc {
  const legacyKind = (raw as { kind?: string }).kind;
  return {
    id: raw.id ?? `mk-${Date.now().toString(36)}`,
    missionSessionId: raw.missionSessionId ?? '',
    title: raw.title,
    body: raw.body,
    category: raw.category ?? (legacyKind && legacyKind !== 'note' ? legacyKind : undefined),
    gapRefs: raw.gapRefs ?? [],
    createdAt: raw.createdAt ?? Date.now(),
  };
}

function emptySession(): MissionKnowledgeSessionData {
  return {
    docs: [],
    structuredAnswers: {},
    automationConsent: DEFAULT_MISSION_AUTOMATION_CONSENT,
    riskApprovals: {},
  };
}

function loadSession(missionSessionId: string): MissionKnowledgeSessionData {
  if (typeof localStorage === 'undefined' || !missionSessionId) return emptySession();
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${missionSessionId}`);
    if (!raw) return emptySession();
    const parsed = JSON.parse(raw) as MissionKnowledgeSessionData & { preferredResumeId?: string };
    if (Array.isArray(parsed)) {
      return {
        docs: parsed.map((d) => normalizeDoc({ ...d, missionSessionId })),
        structuredAnswers: {},
        automationConsent: DEFAULT_MISSION_AUTOMATION_CONSENT,
        riskApprovals: {},
      };
    }
    return {
      docs: (parsed.docs ?? []).map((d) => normalizeDoc({ ...d, missionSessionId })),
      structuredAnswers: parsed.structuredAnswers ?? {},
      automationConsent: normalizeMissionAutomationConsent(parsed.automationConsent),
      riskApprovals: parsed.riskApprovals ?? {},
    };
  } catch {
    return emptySession();
  }
}

function persistSession(missionSessionId: string, data: MissionKnowledgeSessionData) {
  if (typeof localStorage === 'undefined' || !missionSessionId) return;
  localStorage.setItem(`${STORAGE_PREFIX}${missionSessionId}`, JSON.stringify(data));
}

export function buildMissionKnowledgeSummary(
  missionSessionId: string,
  data: MissionKnowledgeSessionData,
  knowledgeRequests: MissionKnowledgeRequest[] = []
): MissionKnowledgeSummary {
  const parts: string[] = [];

  const structured = formatStructuredAnswersForContext(knowledgeRequests, data.structuredAnswers);
  if (structured) parts.push(structured);

  if (data.docs.length > 0) {
    const docBlock = data.docs
      .map((d) => {
        const label = d.category ? `${d.title} (${d.category})` : d.title;
        return `### ${label}\n${d.body.trim()}`;
      })
      .join('\n\n');
    parts.push(docBlock);
  }

  const contextBlock = parts.join('\n\n').slice(0, 12_000);
  return { missionSessionId, docCount: data.docs.length, contextBlock };
}

interface MissionKnowledgeStore extends MissionKnowledgeSessionData {
  missionSessionId: string;
  automationConsent: MissionAutomationConsent;
  riskApprovals: Record<string, MissionRiskApproval>;
  ragAvailable: boolean | null;
  lastSyncAt: number | null;
  setMissionSession: (id: string) => void;
  setAutomationScope: (scope: MissionAutomationScope, enabled: boolean, note?: string) => void;
  approveRiskItem: (itemId: string, note?: string) => void;
  addDoc: (input: {
    title: string;
    body: string;
    category?: string;
    gapRefs?: string[];
  }) => string | null;
  removeDoc: (id: string) => void;
  setStructuredAnswer: (fieldId: string, value: string) => void;
  getSummary: (knowledgeRequests?: MissionKnowledgeRequest[]) => MissionKnowledgeSummary;
  gapIsCovered: (gap: string, knowledgeRequests?: MissionKnowledgeRequest[]) => boolean;
  syncToServer: (rawOutcome?: string) => Promise<void>;
  fetchRetrievedContext: (query: string, topK?: number) => Promise<string>;
}

function sessionSnapshot(state: MissionKnowledgeStore): MissionKnowledgeSessionData {
  return {
    docs: state.docs,
    structuredAnswers: state.structuredAnswers,
    automationConsent: state.automationConsent,
    riskApprovals: state.riskApprovals,
  };
}

let syncTimer: ReturnType<typeof setTimeout> | null = null;

function schedulePolicySync(get: () => MissionKnowledgeStore, rawOutcome?: string) {
  const { missionSessionId } = get();
  if (!missionSessionId) return;
  retainMissionSessionPolicyAsync(missionSessionId, {
    rawOutcome,
    structuredAnswers: get().structuredAnswers,
    automationConsent: get().automationConsent,
    riskApprovals: get().riskApprovals,
  });
}

function scheduleServerSync(get: () => MissionKnowledgeStore) {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    void get().syncToServer();
  }, 700);
}

export const useMissionKnowledgeStore = create<MissionKnowledgeStore>((set, get) => ({
  missionSessionId: '',
  docs: [],
  structuredAnswers: {},
  automationConsent: DEFAULT_MISSION_AUTOMATION_CONSENT,
  riskApprovals: {},
  ragAvailable: null,
  lastSyncAt: null,

  setMissionSession: (id) => {
    const loaded = loadSession(id);
    set({ missionSessionId: id, ...loaded });
  },

  setAutomationScope: (scope, enabled, note) => {
    const { missionSessionId, docs, structuredAnswers, automationConsent } = get();
    if (!missionSessionId) return;
    const nextConsent = setMissionAutomationScope(automationConsent, scope, enabled, note);
    persistSession(missionSessionId, {
      docs,
      structuredAnswers,
      automationConsent: nextConsent,
      riskApprovals: get().riskApprovals,
    });
    set({ automationConsent: nextConsent });
    schedulePolicySync(get);
    recordMissionDossier({
      actor: 'human',
      kind: 'automation_consent',
      summary: enabled ? `Opted in: ${scope}` : `Revoked: ${scope}`,
      action: note?.trim() || undefined,
    });
  },

  approveRiskItem: (itemId, note) => {
    const { missionSessionId, docs, structuredAnswers, automationConsent, riskApprovals } = get();
    if (!missionSessionId || !itemId) return;
    const next: Record<string, MissionRiskApproval> = {
      ...riskApprovals,
      [itemId]: { approvedAt: new Date().toISOString(), note: note?.trim() || undefined },
    };
    persistSession(missionSessionId, {
      docs,
      structuredAnswers,
      automationConsent,
      riskApprovals: next,
    });
    set({ riskApprovals: next });
    schedulePolicySync(get);
    recordMissionDossier({
      actor: 'human',
      kind: 'risk_approval',
      summary: 'Approved risky item for this Mission',
      action: note?.trim() || itemId,
      payload: { itemId },
    });
  },

  addDoc: ({ title, body, category, gapRefs = [] }) => {
    const { missionSessionId, docs, structuredAnswers } = get();
    if (!missionSessionId || !title.trim() || !body.trim()) return null;
    const doc: MissionKnowledgeDoc = {
      id: `mk-${Date.now().toString(36)}`,
      missionSessionId,
      title: title.trim(),
      body: body.trim(),
      category: category?.trim() || undefined,
      gapRefs,
      createdAt: Date.now(),
    };
    const nextDocs = [...docs, doc];
    persistSession(missionSessionId, {
      docs: nextDocs,
      structuredAnswers,
      automationConsent: get().automationConsent,
      riskApprovals: get().riskApprovals,
    });
    set({ docs: nextDocs });
    scheduleServerSync(get);
    return doc.id;
  },

  removeDoc: (id) => {
    const { missionSessionId, docs, structuredAnswers } = get();
    const nextDocs = docs.filter((d) => d.id !== id);
    persistSession(missionSessionId, {
      docs: nextDocs,
      structuredAnswers,
      automationConsent: get().automationConsent,
      riskApprovals: get().riskApprovals,
    });
    set({ docs: nextDocs });
    void deleteMissionDocOnServer(missionSessionId, id);
    scheduleServerSync(get);
  },

  setStructuredAnswer: (fieldId, value) => {
    const { missionSessionId, docs, structuredAnswers } = get();
    const next = { ...structuredAnswers, [fieldId]: value };
    persistSession(missionSessionId, {
      docs,
      structuredAnswers: next,
      automationConsent: get().automationConsent,
      riskApprovals: get().riskApprovals,
    });
    set({ structuredAnswers: next });
    scheduleServerSync(get);
  },

  getSummary: (knowledgeRequests = []) =>
    buildMissionKnowledgeSummary(get().missionSessionId, sessionSnapshot(get()), knowledgeRequests),

  gapIsCovered: (gap, knowledgeRequests = []) => {
    const lower = gap.toLowerCase();
    const { docs, structuredAnswers } = get();

    const linkedRequest = knowledgeRequests.find((r) => r.gapRef.toLowerCase() === lower);
    if (linkedRequest && requestIsComplete(linkedRequest, structuredAnswers)) {
      return true;
    }

    return docs.some(
      (d) =>
        d.gapRefs.some((r) => r.toLowerCase() === lower) ||
        d.title.toLowerCase().includes(lower.slice(0, 24)) ||
        d.body.toLowerCase().includes(lower.slice(0, 32))
    );
  },

  syncToServer: async (rawOutcome) => {
    const { missionSessionId } = get();
    if (!missionSessionId) return;
    const result = await syncMissionSessionToServer(
      missionSessionId,
      sessionSnapshot(get()),
      rawOutcome
    );
    set({
      ragAvailable: result.message?.includes('not configured') ? false : result.ok,
      lastSyncAt: result.synced > 0 || result.embeddedChunks ? Date.now() : get().lastSyncAt,
    });
    schedulePolicySync(get, rawOutcome);
  },

  fetchRetrievedContext: async (query, topK = 8) => {
    const { missionSessionId } = get();
    if (!missionSessionId || !query.trim()) return '';
    const result = await retrieveMissionContext(missionSessionId, query, topK);
    if (result.message?.includes('not configured')) {
      set({ ragAvailable: false });
    } else if (result.chunkCount > 0) {
      set({ ragAvailable: true });
    }
    return result.contextBlock;
  },
}));

export function createMissionSessionId(): string {
  return `mission-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function missionKnowledgeContextBlock(knowledgeRequests: MissionKnowledgeRequest[] = []): string | undefined {
  const block = useMissionKnowledgeStore.getState().getSummary(knowledgeRequests).contextBlock.trim();
  return block || undefined;
}
